import React, { useState, useEffect, useRef, useMemo } from "react";
import { DateTime } from "luxon";
import SearchBar from "../utils/SearchBar";
import DataTable from "../utils/DataTable";
import ActionBar from "../utils/ActionBar";
import ExportDropdown from "../utils/ExportDropdown";
import ImportModal from "../utils/ImportModal";
import FilterMenu from "../utils/FilterMenu";
import InteractionContextMenu from "../utils/InteractionContextMenu";
import AssignmentContextMenu from "../utils/AssignmentContextMenu";
import RightDetails from "../utils/RightDetails";
import "../utils/RightDetails.css";
import MassReassignMenu from "../utils/MassReassignMenu";
import ViewFilter from "../utils/ViewFilter";
import { filterDataForUser } from "../../layouts/roleUtils";
import api from "../../api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileImport,
  faPlus,
  faEye,
  faEyeSlash,
  faFilter
} from "@fortawesome/free-solid-svg-icons";
import { FaExchangeAlt, FaArchive } from "react-icons/fa";
import AddressContextMenu from "../utils/AddressContextMenu";
import { toUTC, getCurrentDateTime } from "../../utils/dateUtils";

const Page = ({
  tableName,     
  fromPage,    
  columns,       
  showToast = (msg, type) => console.log(msg, type),
  currentUser,
  onDetailPanelChange,
}) => {
  const [data, setData] = useState([]);
  const [users, setUsers] = useState([]);
  const [existingCompanies, setExistingCompanies] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [availableFields, setAvailableFields] = useState([]);
  const [viewArchived, setViewArchived] = useState(false);
  const [isReassignMenuOpen, setIsReassignMenuOpen] = useState(false);
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const [detailsData, setDetailsData] = useState(null);
  const [currentView, setCurrentView] = useState("myself"); // Default to "myself" view
  const [allPeopleData, setAllPeopleData] = useState([]); // All people for autocomplete
  const tableRef = useRef(null);
  
  // Notify parent when detail panel opens/closes
  useEffect(() => {
    if (onDetailPanelChange) {
      onDetailPanelChange(!!detailsData);
    }
  }, [detailsData, onDetailPanelChange]);

  const defaultFilters = {
    statuses: [],
    assignedTo: [],
    vip: "all",
    hiddenStatuses: ["Closed Lost"],
    searchQuery: ""
  };
  const [filters, setFilters] = useState(defaultFilters);

  const [interactionContextMenu, setInteractionContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    rowData: null,
  });

  const [assignmentContextMenu, setAssignmentContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    personId: null,
    assignmentType: null,
    currentAssignments: [],
  });

  const fetchData = async () => {
    try {
      const archiveValue = viewArchived ? 1 : 0;
      const params = new URLSearchParams();
      params.append('archive', archiveValue);
      
      // Add view parameter for role-based filtering
      if (currentView) {
        params.append('view', currentView);
      }
      
      const response = await api.get(`/${tableName}?${params.toString()}`);
      setData(response.data);
    } catch (error) {
      console.error(`Error fetching ${tableName}:`, error);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await api.get("/companies");
      setExistingCompanies(response.data);
    } catch (error) {
      console.error("Error fetching companies:", error);
    }
  };

  const fetchUsers = async () => {
    try {
      if (currentUser && currentUser.role === "team lead") {
        const response = await api.get(`/team-users?teamLeadId=${currentUser.id}`);
        setUsers(response.data);
      } else {
        const response = await api.get("/userinfo");
        setUsers(response.data);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [viewArchived, tableName, currentView]);

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [currentUser]);

  // Fetch all people data for autocomplete (duplicate detection)
  useEffect(() => {
    const fetchAllPeopleForAutocomplete = async () => {
      try {
        const response = await api.get('/people/autocomplete/all');
        setAllPeopleData(response.data);
      } catch (error) {
        console.error('Error fetching all people for autocomplete:', error);
      }
    };
    
    if (tableName === 'people') {
      fetchAllPeopleForAutocomplete();
    }
  }, [tableName]);

  // Add event listener for opening company details from PeopleDetails
  useEffect(() => {
    const handleOpenCompanyDetails = (event) => {
      if (event.detail && event.detail.company) {
        setDetailsData(event.detail.company);
      }
    };
    
    window.addEventListener('openCompanyDetails', handleOpenCompanyDetails);
    
    return () => {
      window.removeEventListener('openCompanyDetails', handleOpenCompanyDetails);
    };
  }, []);

  useEffect(() => {
    const fetchAvailableFields = async () => {
      try {
        const response = await api.get(`/general/columns/${tableName}`);
        const fields = response.data.map(col => col.COLUMN_NAME);
        setAvailableFields(fields);
      } catch (error) {
        console.error("Error fetching available fields:", error);
      }
    };
    fetchAvailableFields();
  }, [tableName]);


  const filteredData = useMemo(() => {
    let filtered = data;
    if (filters.statuses.length > 0) {
      filtered = filtered.filter(item => filters.statuses.includes(item.status));
    }
    if (filters.assignedTo.length > 0) {
      filtered = filtered.filter(item => filters.assignedTo.includes(item.assigned_to));
    }
    if (filters.vip !== "all") {
      const isVip = filters.vip === "yes";
      filtered = filtered.filter(item => !!item.vip === isVip);
    }
    if (filters.hiddenStatuses.length > 0) {
      filtered = filtered.filter(item => !filters.hiddenStatuses.includes(item.status));
    }
    if (filters.searchQuery) {
      const searchLower = filters.searchQuery.toLowerCase();
      filtered = filtered.filter(item =>
        Object.values(item)
          .filter(val => typeof val === "string")
          .some(val => val.toLowerCase().includes(searchLower))
      );
    }
    // Note: Role-based filtering is now handled by the backend via view parameter
    // The filterDataForUser is no longer needed since backend does the filtering
    // if (currentUser && tableName === "people") {
    //   filtered = filterDataForUser(filtered, currentUser, users);
    // }
      
    return filtered;
  }, [data, filters]);

  // Handle view changes
  const handleViewChange = (newView) => {
    setCurrentView(newView);
    setSelectedIds([]); // Clear selections when changing views
  };

  const mergedColumns = useMemo(() => {
    return columns.map(col => {
      if (col.accessor === "assigned_to" || col.accessor === "created_by") {
        return { ...col, chipDropdownOptions: users };
      }
      if (col.accessor === "name" && col.buttonOnHover) {
        return {
          ...col,
          buttonOnHover: {
            ...col.buttonOnHover,
            onClick: (rowData) => setDetailsData(rowData),
          },
        };
      }
      // Add click handlers for assignment columns
      if (col.clickable && col.assignmentType) {
        return {
          ...col,
          Cell: ({ row }) => {
            const assignments = row.original?.assignments?.[col.assignmentType] || [];
            const firstAssignment = assignments[0];
            const remainingCount = assignments.length - 1;
            
            return (
              <div 
                style={{ 
                  display: "flex", 
                  flexWrap: "nowrap",
                  gap: "4px",
                  cursor: "pointer",
                  padding: "4px",
                  borderRadius: "4px",
                  minHeight: "28px",
                  alignItems: "center",
                  transition: "background 0.2s ease",
                  overflow: "hidden"
                }}
                className="assignment-cell"
                title={assignments.length > 0 
                  ? `${assignments.map(a => `${a.first_name} ${a.last_name}`).join(', ')}` 
                  : `Click to assign ${col.assignmentType} users`}
                onClick={(e) => handleAssignmentCellClick(e, row.original, col.assignmentType)}
              >
                {assignments.length === 0 ? (
                  <span style={{ color: "#999", fontSize: "12px" }}>Click to assign</span>
                ) : (
                  <>
                    <div
                      style={{
                        backgroundColor: firstAssignment.active === "n" ? "#F08080" : (firstAssignment.chip_color || "#e0e0e0"),
                        padding: "4px 8px",
                        borderRadius: "12px",
                        fontSize: "12px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {firstAssignment.first_name} {firstAssignment.last_name}
                    </div>
                    {remainingCount > 0 && (
                      <div
                        style={{
                          backgroundColor: "#e0e0e0",
                          padding: "4px 8px",
                          borderRadius: "12px",
                          fontSize: "12px",
                          fontWeight: "600",
                          color: "#666",
                          whiteSpace: "nowrap",
                        }}
                      >
                        +{remainingCount}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          }
        };
      }
      return col;
    });
  }, [columns, users, setDetailsData]);
  
  

  const handleCellUpdate = async (id, field, value) => {
    let updateObj = { [field]: value };
    if (["last_contacted", "follow_up_date"].includes(field) && value) {
      // No need for manual conversion - our utility handles this
      updateObj[field] = value; // Value is already in UTC ISO format from DataTable component
    }
    
    // Check if this is a status change to "Closed Won"
    const isClosedWonUpdate = field === "status" && value === "Closed Won";
    
    setData(prev =>
      prev.map(item => {
        if (item.id === id) {
          let updated = { ...item, [field]: value };
          if (field === "status" && value === "Contacted") {
            // Use our utility to get current time in UTC
            const currentDateTime = getCurrentDateTime().toUTC().toISO();
            updated = { ...updated, last_contacted: currentDateTime };
            updateObj.last_contacted = currentDateTime;
          }
          if (detailsData && detailsData.id === id) {
            setDetailsData(updated);
          }
          if (field === "assigned_to") {
            const selectedUser = users.find(u => u.id === parseInt(value, 10));
            if (selectedUser) {
              updated.first_name = selectedUser.first_name;
              updated.last_name = selectedUser.last_name;
              updated.chip_color = selectedUser.chip_color;
              updated.active = selectedUser.active;
            }
          }
          return updated;
        }
        return item;
      })
    );
    
    try {
      const response = await api.put(`/${tableName}/${id}`, updateObj);
      
      // If status changed to "Closed Won" and a sale was created, notify user
      if (isClosedWonUpdate && response.data && response.data.sale_id) {
        console.log("Sale created with ID:", response.data.sale_id);
        showToast("Sale created automatically. View it in the Sales tab.", "success");
      }
    } catch (error) {
      console.error(`Error updating ${tableName}:`, error);
    }
  };

  const handleMassStatusChange = (newStatus, selectedIds) => {
    setData(prev =>
      prev.map(item =>
        selectedIds.includes(item.id.toString())
          ? { ...item, status: newStatus }
          : item
      )
    );
    selectedIds.forEach(id => {
      api.put(`/${tableName}/${id}`, { status: newStatus }).catch(error => {
        console.error(`Error updating ${tableName}:`, error);
      });
    });
  };

  // Assignment handlers
  const handleAssignmentCellClick = (e, row, assignmentType) => {
    e.stopPropagation();
    const assignments = row.assignments || { primary: [], secondary: [] };
    setAssignmentContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      personId: row.id,
      assignmentType: assignmentType,
      currentAssignments: assignments[assignmentType] || [],
    });
  };

  const handleAddAssignment = async (personId, userId, assignmentType) => {
    try {
      const response = await api.post("/people-assignments", {
        person_id: personId,
        user_id: userId,
        assignment_type: assignmentType
      });

      if (response.data) {
        // Update local data
        setData(prev =>
          prev.map(item => {
            if (item.id === personId) {
              const updatedAssignments = { ...item.assignments };
              if (!updatedAssignments[assignmentType]) {
                updatedAssignments[assignmentType] = [];
              }
              updatedAssignments[assignmentType].push(response.data);
              return { ...item, assignments: updatedAssignments };
            }
            return item;
          })
        );
        
        // Update assignment context menu
        setAssignmentContextMenu(prev => ({
          ...prev,
          currentAssignments: [...prev.currentAssignments, response.data]
        }));

        showToast(`User assigned as ${assignmentType}`, "success");
      }
    } catch (error) {
      console.error("Error adding assignment:", error);
      showToast(error.response?.data?.error || "Failed to add assignment", "error");
      throw error;
    }
  };

  const handleRemoveAssignment = async (assignmentId) => {
    try {
      await api.delete(`/people-assignments/${assignmentId}`);

      // Update local data
      setData(prev =>
        prev.map(item => {
          if (item.assignments) {
            const updatedAssignments = {
              primary: item.assignments.primary.filter(a => a.assignment_id !== assignmentId),
              secondary: item.assignments.secondary.filter(a => a.assignment_id !== assignmentId)
            };
            return { ...item, assignments: updatedAssignments };
          }
          return item;
        })
      );

      // Update assignment context menu
      setAssignmentContextMenu(prev => ({
        ...prev,
        currentAssignments: prev.currentAssignments.filter(a => a.assignment_id !== assignmentId)
      }));

      showToast("Assignment removed", "success");
    } catch (error) {
      console.error("Error removing assignment:", error);
      showToast("Failed to remove assignment", "error");
      throw error;
    }
  };

  const handleMassReassign = async (assignmentData) => {
    // Check if this is multi-user assignment mode or legacy mode
    if (typeof assignmentData === "object" && assignmentData.mode === "multi") {
      // Multi-user assignment mode
      const { primary, secondary, selectedIds: ids } = assignmentData;
      
      try {
        const response = await api.post("/mass-assignments", {
          person_ids: ids,
          primary_user_ids: primary,
          secondary_user_ids: secondary
        });
        
        // Refresh data to show new assignments
        await fetchData();
        
        setSelectedIds([]);
        showToast(
          `Mass assignment completed: ${response.data.assigned_people} people assigned, ${response.data.assignments_created} assignments created`,
          "success"
        );
      } catch (error) {
        console.error("Error in mass assignment:", error);
        showToast("Error creating mass assignments.", "error");
      }
    } else {
      // Legacy single-user reassignment mode
      const newUserId = assignmentData;
      
      if (!newUserId) {
        alert("Please select a user to reassign.");
        return;
      }
      
      try {
        await Promise.all(
          selectedIds.map(id =>
            api.put(`/${tableName}/${id}`, { assigned_to: newUserId })
          )
        );
        
        if (tableName === "people") {
          setData(prev =>
            prev.map(item => {
              if (selectedIds.includes(item.id.toString())) {
                const updated = { ...item, assigned_to: newUserId };
                const newUser = users.find(u => u.id === parseInt(newUserId, 10));
                if (newUser) {
                  updated.first_name = newUser.first_name;
                  updated.last_name = newUser.last_name;
                  updated.chip_color = newUser.chip_color;
                  updated.active = newUser.active;
                }
                return updated;
              }
              return item;
            })
          );
        }
        
        setSelectedIds([]);
        showToast(`${tableName} reassigned successfully!`, "success");
      } catch (error) {
        console.error("Error in mass reassign:", error);
        showToast("Error reassigning.", "error");
      }
    }
  };

  const handleMassArchive = async () => {
    if (selectedIds.length === 0) return;
    try {
      await api.put(`/general/updateArchive/${tableName}`, {
        ids: selectedIds,
        archiveValue: 1,
      });
      setData(prev => prev.filter(item => !selectedIds.includes(item.id.toString())));
      setSelectedIds([]);
      showToast(`${tableName} archived successfully!`, "success");
    } catch (error) {
      console.error(`Error archiving ${tableName}:`, error);
      showToast("Error archiving.", "error");
    }
  };

  const handleMassUnarchive = async () => {
    if (selectedIds.length === 0) return;
    try {
      await api.put(`/general/updateArchive/${tableName}`, {
        ids: selectedIds,
        archiveValue: 0,
      });
      setData(prev => prev.filter(item => !selectedIds.includes(item.id.toString())));
      setSelectedIds([]);
      showToast(`${tableName} unarchived successfully!`, "success");
    } catch (error) {
      console.error(`Error unarchiving ${tableName}:`, error);
      showToast("Error unarchiving.", "error");
    }
  };

  const handleImport = async (importedData) => {
    try {
      const dataWithFields = importedData.map(record => ({
        ...record,
        created_by: currentUser ? currentUser.id : null,
        assigned_to: record.assigned_to || (currentUser ? currentUser.id : null),
      }));
      
      // Use specialized import endpoint for people table to handle assignments
      if (tableName === "people") {
        const response = await api.post("/people-import", dataWithFields);
        showToast(
          `Import completed: ${response.data.inserted} people imported, ${response.data.assignments_created} assignments created`, 
          "success"
        );
      } else {
        await api.post(`/general/import/${tableName}`, dataWithFields);
        showToast(`${tableName} imported successfully!`, "success");
      }
      
      const fetchResponse = await api.get(`/${tableName}`);
      setData(fetchResponse.data);
    } catch (error) {
      console.error(`Error importing ${tableName}:`, error);
      showToast(`Error importing ${tableName}.`, "error");
    }
  };

  const handleArchiveRow = async (id) => {
    try {
      await api.put(`/general/updateArchive/${tableName}`, {
        ids: [id.toString()],
        archiveValue: 1,
      });
      setData(prev => prev.filter(item => item.id.toString() !== id.toString()));
      showToast("Record archived successfully!", "success");
    } catch (error) {
      console.error(`Error archiving record from ${tableName}:`, error);
      showToast("Error archiving record.", "error");
    }
  };

  const handleAddRow = async () => {
    const newRow = {
      status: "New",
      vip: 0,
      assigned_to: currentUser ? currentUser.id : null,
      created_by: currentUser ? currentUser.id : null,
    };
    // Fill any undefined fields with null using the passed columns.
    columns.forEach(col => {
      if (col.accessor && col.accessor !== "selection" && newRow[col.accessor] === undefined) {
        newRow[col.accessor] = null;
      }
    });
    try {
      const response = await api.post(`/${tableName}`, newRow);
      const insertedRow = { ...newRow, id: response.data.id };
      setData(prev => [insertedRow, ...prev]);
      showToast("New row added.", "info");
    } catch (error) {
      console.error(`Error adding new row to ${tableName}:`, error);
      showToast("Error adding new row.", "error");
    }
  };

  const handleCreateCompany = async (companyName, rowData) => {
    if (!companyName) return;
    const confirmed = window.confirm(`Create company "${companyName}"?`);
    if (!confirmed) return;
    try {
      const postResponse = await api.post("/companies", { name: companyName });
      showToast(`Company "${companyName}" created successfully!`, "success");
      const newCompanyId = postResponse.data.id;
      const getResponse = await api.get(`/companies/${newCompanyId}`);
      const newCompany = { ...getResponse.data, fromPage: "Company" };
      setDetailsData(newCompany);
      const companiesResponse = await api.get("/companies");
      setExistingCompanies(companiesResponse.data);
      if (tableName === "people") {
        setData(prev =>
          prev.map(person =>
            person.id === rowData.id ? { ...person, company: newCompany.name } : person
          )
        );
      }
    } catch (error) {
      console.error("Error creating company:", error);
      showToast("Error creating company", "error");
    }
  };

  const handleOpenReassignMenu = () => {
    setIsReassignMenuOpen(true);
  };

  const handleSaveBusinessCard = async (contactData) => {
    try {
      // Add current user as assigned_to if available
      if (currentUser && currentUser.id) {
        contactData.assigned_to = currentUser.id;
      }
      
      // Add status if not present
      if (!contactData.status) {
        contactData.status = "New";
      }

      const response = await api.post(`/${tableName}`, contactData);
      const insertedRow = { ...contactData, id: response.data.id };
      setData(prev => [insertedRow, ...prev]);
      showToast("Contact from business card saved successfully!", "success");
      fetchData(); // Refresh the data
    } catch (error) {
      console.error("Error saving business card contact:", error);
      showToast("Error saving contact from business card", "error");
      throw error; // Re-throw to let the component handle it
    }
  };

  return (
    <div className={tableName}>
      <SearchBar
        placeholder={`Search ${tableName}...`}
        onSearch={(query) =>
          setFilters(prev => ({ ...prev, searchQuery: query }))
        }
      />
      
      {/* View Filter - only show for people table and if user has role access */}
      {tableName === "people" && currentUser && (
        <ViewFilter
          currentView={currentView}
          onViewChange={handleViewChange}
          userRole={currentUser.role}
          className="page-view-filter"
        />
      )}
      
      <div style={{ position: "relative" }}>
        <ActionBar
          selectedCount={selectedIds.length}
          totalCount={filteredData.length}
          entityName={tableName}
          archivedView={viewArchived}
        >
          <button onClick={handleAddRow} title="Add Row">
            <FontAwesomeIcon icon={faPlus} />
          </button>
          <button onClick={() => setIsFilterMenuOpen(true)} title="Filter">
            <FontAwesomeIcon icon={faFilter} />
          </button>
          <button
            onClick={handleOpenReassignMenu}
            disabled={selectedIds.length === 0}
            title="Reassign Selected"
          >
            <FaExchangeAlt />
          </button>
          {viewArchived ? (
            <button
              onClick={handleMassUnarchive}
              disabled={selectedIds.length === 0}
              title="Unarchive Selected"
            >
              Unarchive
            </button>
          ) : (
            <button
              onClick={handleMassArchive}
              disabled={selectedIds.length === 0}
              title="Archive Selected"
            >
              <FaArchive />
            </button>
          )}
          <button onClick={() => setIsImportModalOpen(true)} title="Upload">
            <FontAwesomeIcon icon={faFileImport} />
          </button>
          <button 
            onClick={() => setDetailsData({ __isBusinessCardScanner: true })} 
            title="Scan Business Card"
          >
            📇
          </button>
          <ExportDropdown
            selectedLeadIds={selectedIds}
            filteredPeople={filteredData}
            columns={mergedColumns}
            tableRef={tableRef}
            showToast={showToast}
          />
          <button
            onClick={() => setViewArchived(!viewArchived)}
            title="Toggle Archived View"
          >
            <FontAwesomeIcon icon={viewArchived ? faEyeSlash : faEye} />
          </button>
        </ActionBar>
        {isReassignMenuOpen && (
          <div className="menu-overlay">
            <MassReassignMenu
              selectedLeadIds={selectedIds}
              leads={data || []}
              users={users || []}
              onClose={() => setIsReassignMenuOpen(false)}
              onReassign={(userId) => handleMassReassign(userId)}
            />
          </div>
        )}
        {isFilterMenuOpen && (
          <div className="menu-overlay">
            <FilterMenu
              filters={filters}
              setFilters={setFilters}
              onClose={() => setIsFilterMenuOpen(false)}
              statuses={[
                "New",
                "Contacted",
                "Qualified",
                "Proposal Sent",
                "Closed Won",
                "Closed Lost",
              ]}
              users={users}
            />
          </div>
        )}
      </div>
      <div ref={tableRef}>
      <DataTable
    columns={mergedColumns}         // The merged columns from People.js (or your source)
    data={filteredData}             // Data fetched and filtered from your API
    onCellUpdate={handleCellUpdate}
    onMassStatusChange={handleMassStatusChange}
    highlightRowOnEdit={true}
    onSelectionChange={setSelectedIds}
    enableRowContextMenu={true}
    onOpenDetails={setDetailsData}
    onCreateCompany={handleCreateCompany}
    existingCompanies={existingCompanies}
    allPeopleData={allPeopleData}   // All people for autocomplete duplicate detection
    stickyHeader={true}             // Enable sticky header
    getRowContextMenuOptions={(rowData) => {
      const statusColumn = mergedColumns.find(col => col.accessor === "status");
      const tableStatusOptions = statusColumn ? statusColumn.DropdownOptions : [];
      return [
        { label: "View Details", onClick: () => setDetailsData(rowData) },
        {
          label: "Add Interaction",
          onClick: (e) => {
            e.stopPropagation();
            const offsetX = -20;
            const offsetY = -20;
            const x = e?.clientX !== undefined ? e.clientX + offsetX : 225 + offsetX;
            const y = e?.clientY !== undefined ? e.clientY + offsetY : 225 + offsetY;
            setInteractionContextMenu({
              visible: true,
              x,
              y,
              rowData,
              statusOptions: tableStatusOptions
            });
          }
        },
        { label: "Archive Row", onClick: () => handleArchiveRow(rowData.id) },
      ];
    }}
  />
        <ImportModal
          isOpen={isImportModalOpen}
          onClose={() => setIsImportModalOpen(false)}
          onImport={handleImport}
          availableFields={availableFields}
          existingPeople={data}
          showToast={showToast}
          users={users}
        />
        {interactionContextMenu.visible && (
          <InteractionContextMenu
            x={interactionContextMenu.x}
            y={interactionContextMenu.y}
            rowData={interactionContextMenu.rowData}
            statusOptions={
              mergedColumns.find(col => col.accessor === "status")?.DropdownOptions || []
            }
            showToast={showToast}
            onSubmit={async (payload) => {
              try {
                await api.post("/interactions", payload);
                showToast("Interaction saved successfully!", "success");
                if (payload.new_status) {
                  handleCellUpdate(
                    interactionContextMenu.rowData.id,
                    "status",
                    payload.new_status
                  );
                }
                if (
                  ["Call", "Email", "Meeting"].includes(payload.interaction) ||
                  (payload.sub_option &&
                    ["Call", "Email", "Meeting"].some(term =>
                      payload.sub_option.includes(term)
                    ))
                ) {
                  const currentDateTime = DateTime.local().toUTC().toISO();
                  handleCellUpdate(
                    interactionContextMenu.rowData.id,
                    "last_contacted",
                    currentDateTime
                  );
                  const formattedFollowUp = payload.follow_up_date
                    ? DateTime.fromISO(payload.follow_up_date, { zone: "local" }).toUTC().toISO()
                    : "";
                  handleCellUpdate(
                    interactionContextMenu.rowData.id,
                    "follow_up_date",
                    formattedFollowUp
                  );
                }
              } catch (error) {
                console.error("Error saving interaction:", error);
                showToast("Error saving interaction", "error");
              }
              setInteractionContextMenu(prev => ({ ...prev, visible: false }));
            }}
            onClose={() =>
              setInteractionContextMenu(prev => ({ ...prev, visible: false }))
            }
          />
        )}

        {assignmentContextMenu.visible && (
          <AssignmentContextMenu
            x={assignmentContextMenu.x}
            y={assignmentContextMenu.y}
            personId={assignmentContextMenu.personId}
            assignmentType={assignmentContextMenu.assignmentType}
            currentAssignments={assignmentContextMenu.currentAssignments}
            availableUsers={users}
            onClose={() => setAssignmentContextMenu(prev => ({ ...prev, visible: false }))}
            onAddAssignment={handleAddAssignment}
            onRemoveAssignment={handleRemoveAssignment}
            showToast={showToast}
          />
        )}

        {detailsData && (
          <div className="right-details-container">
            {console.log("Rendering RightDetails with props:", {
              fromPage: detailsData.fromPage || fromPage,
              dataId: detailsData.id,
              data: detailsData
            })}
            <RightDetails
              data={detailsData}
              columns={mergedColumns}
              fromPage={detailsData.fromPage || fromPage}
              isPeopleParent={tableName === "people"}
              users={users}
              currentUser={currentUser}
              onClose={() => setDetailsData(null)}
              onSave={async (updatedData) => {
                // Check if this is a new business card contact (no ID)
                if (!updatedData.id && detailsData.__isBusinessCardScanner) {
                  return handleSaveBusinessCard(updatedData);
                }
                
                // Otherwise it's an update
                setData(prev =>
                  prev.map(item =>
                    item.id === updatedData.id ? { ...item, ...updatedData } : item
                  )
                );
                try {
                  const response = await api.put(`/${tableName}/${updatedData.id}`, updatedData);
                  
                  // If this is a status change to "Closed Won" and a sale was created, notify user
                  if (updatedData.status === "Closed Won" && response.data && response.data.sale_id) {
                    console.log("Sale created with ID:", response.data.sale_id);
                    showToast("Sale created automatically. View it in the Sales tab.", "success");
                  } else {
                    showToast("Details updated successfully!", "success");
                  }
                  
                  return response;
                } catch (error) {
                  console.error(`Error updating ${tableName} details:`, error);
                  showToast("Error updating details", "error");
                  throw error;
                }
              }}
              showToast={showToast}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Page;
